import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Lottie from "lottie-react";

import shareIcon from "../../../assets/images/share.png";
import closeIcon from "../../../assets/images/close.png";
import LottieAnimation from "../../../assets/json/sezione-05-popup-01-it.json";
import arrowIconWhite from "../../../assets/images/arrowIconWhite.png";

/* ===================== DATI CONTENUTI ===================== */
const INPUT_CONTENT = {
  Finanziario: [
    "Raccolta premi complessiva",
    "Asset under Management",
    "Integrazione dei criteri ESG nei processi di investimento",
  ],
  Produttivo: [
    "Rete agenziale",
    "Sedi direzionali",
    "Centri liquidazioni danni",
    "Broker, intermediari e consulenti finanziari",
    "Home banking",
    "Sportelli bancari e spazi Banca Reale",
    "Immobili di proprietà"
  ],
  Intellettuale: [
    "Know how aziendale e tecnologico",
    "Digitalizzazione delle attività della Rete Agenziale",
    "Reale Group Digital Factory",
    "Corporate Venturing, Ecosistemi e Partnership e Reale Lab 1828",
    "Investimenti in innovazione sostenibile IA",
  ],
  Umano: [
    "Persone   del Gruppo",
    "Benefit previsti per i dipendenti",
    "Scuola di formazione del Gruppo Academy",
    "Valutazione periodica delle performance e dello sviluppo professionale",
    "Accessibilità",
    "Cultura organizzativa della diversità e dell’inclusione",
  ],
  "Sociale/Relazionale": [
    "Soci/Assicurati-Clienti",
    "Fornitori e partner",
    "Volontariato d’impresa",
    "Erogazioni liberali e Sponsorizzazioni",
    "Relazioni istituzionali",
    "Iniziative filantropiche d’impatto di Reale Foundation",
  ],
  Naturale: [
    "Monitoraggio consumi energetici",
    "Azioni di risparmio energetico",
    "Calcolo della CO₂",
    "Piani di azione per la riduzione degli impatti ambientali diretti",
    "Piano di Decarbonizzazione",
    "Sistema di Gestione Ambientale",
  ],
};

const OUTPUT_CONTENT = {
  Finanziario: [
    "Utile consolidato",
    "Valore distribuito",
    "Investimenti sostenibili",
    "Indice di solvibilità",
    "Vantaggi Mutualistici",
  ],
  Produttivo: [
    "Eccellenza tecnica",
    "Prodotti con caratteristiche ambientali e/o sociali",
    "Pronta risposta alle nuove esigenze di operatività a distanza dei Soci/Assicurati-Clienti",
    "Continuità del servizio",
    "Immobili certificati (es. LEED Gold, Platinum Torino, Gold e Silver sedi spagnole)",
  ],
  Intellettuale: [
    "Efficienza nei processi",
    "Partnership innovative e sviluppo di ecosistemi",
    "Ridisegno strutturato delle modalità di lavoro",
  ],
  Umano: [
    "Benessere dei Dipendenti",
    "Inclusione & Employer Branding",
    "Promozione delle diversità e dell’inclusione",
    "Ambiente di lavoro sicuro e accessibile",
    "Employer Reputation",
    "Trust Index",
  ],
  "Sociale/Relazionale": [
    "Corporate Reputation",
    "Sustainability Index",
    "Soddisfazione cliente",
    "Misurazione di impatto",
    "Misurazione del social return on investment – SROI",
  ],
  Naturale: [
    "Carbon footprint",
    "Riduzione dei consumi",
    "Gestione efficiente dei rifiuti",
    "Riduzione delle emissioni",
    "Certificazione UNI EN ISO 14001",
  ],
};

/* ===================== COMPONENTI UI ===================== */

/** Stack verticale dei toggle (INPUT/OUTPUT) */
function ToggleStack({ section = "input", activeKey, onOpen, label }) {
  const KEYS = section === "input" ? Object.keys(INPUT_CONTENT) : Object.keys(OUTPUT_CONTENT);

  return (
    <div
      className="
        pointy-box  pointy-wide
        mb-8                             /* 👈 spazio per la punta */
        flex flex-col gap-1 border-[2px] border-[#c66529]
        p-4 md:p-8
      "
      style={{ borderRadius: "15px 15px 50px 50px" }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {KEYS.map((k) => {
        const isOpen = activeKey === k;
        return (
          <button
            key={k}
            type="button"
            aria-expanded={isOpen}
            onClick={(e) => onOpen(isOpen ? null : { key: k, el: e.currentTarget })}
            className="
              flex items-center justify-between gap-3
              text-[#0E3761] text-[12px] md:text-[26px]
              hover:opacity-90 transition
            "
          >
            <span>{k}</span>
            <span>{isOpen ? "–" : "+"}</span>
          </button>
        );
      })}
      <p className="text-center text-[14px] md:text-[30px] text-[#c66529] font-bold">{label}</p>
    </div>
  );
}

/** Pannello flottante arancione ancorato sopra al toggle cliccato */
function FloatingPanel({ title, items = [], onClose, anchorY }) {
  return (
    <div
      className="absolute left-1/2 -translate-x-1/2 z-[5] w-[92%] max-w-[620px]"
      style={{ top: anchorY }} // <-- allineamento al bordo superiore del filetto
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="relative bg-[#c66529] text-white rounded-sm shadow-lg px-5 md:px-8 py-5"
        role="dialog"
        aria-label={title}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-2 text-white/90 text-xl leading-none"
          aria-label="Chiudi pannello"
          title="Chiudi"
        >
          –
        </button>

        {/* TITOLETTO */}
        <div className="text-left text-[12px] md:text-[20px] font-bold mb-3">
          {title}
        </div>

        {/* TESTO allineato a sinistra + interlinea ridotta */}
        <div className="text-left text-[10px] md:text-[18px] leading-[1.15] whitespace-pre-line">
          {items.map((t, i) => (
            <div key={i} className="mb-2 last:mb-0">
              {t}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ===================== POPUP ===================== */

export default function ModelloBusiness({ isOpen, onClose }) {
  const overlayRef = useRef(null);
  const stageRef = useRef(null);

  const inputBoxRef = useRef(null);
  const outputBoxRef = useRef(null);

  const [panel, setPanel] = useState(null);

  // ESC + blocco scroll quando il popup è aperto
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    if (isOpen) {
      document.body.classList.add("overflow-hidden");
      window.addEventListener("keydown", onKey);
    }
    return () => {
      document.body.classList.remove("overflow-hidden");
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const onShare = (popupId) => {
    const url = new URL(window.location.href);
    url.searchParams.set("popup", popupId);

    if (navigator.share) {
      navigator
        .share({ title: "Modello di business", url: url.toString() })
        .catch(() => {});
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(url.toString()).catch(() => {});
    }
  };

  // calcola e apre pannello ancorato alla voce cliccata
  const openFrom = (section) => (payload) => {
  if (!payload) return setPanel(null);
  const { key } = payload;

  const stageEl = stageRef.current;
  const refBox = section === "input" ? inputBoxRef.current : outputBoxRef.current;

  let anchorY = 140; // fallback
  if (stageEl && refBox) {
    const boxRect = refBox.getBoundingClientRect();
    const stageRect = stageEl.getBoundingClientRect();
    // distanza del bordo superiore del “filetto” rispetto al top dello stage
    anchorY = boxRect.top - stageRect.top;
  }

  setPanel({ section, key, anchorY });
};

  const activeItems =
    panel?.section === "input"
      ? INPUT_CONTENT[panel?.key || ""]
      : OUTPUT_CONTENT[panel?.key || ""];

  return createPortal(
    
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[1001] bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-3 md:p-6"
      onMouseDown={(e) => {
        if (e.target === overlayRef.current) onClose?.();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Modello di business"
    >
      <div
        className="
          relative w-full md:w-[1280px] bg-[#E7E3DF] p-6 md:p-8
          max-h-[90vh] overflow-y-auto overscroll-contain
        "
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Azioni in alto */}
        <div className="sticky top-3 z-10 flex items-center gap-2 justify-end pr-3">
          <button
            type="button"
            onClick={() => onShare("ModelloBusiness")}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-[#0E3761] bg-white/90 border border-[#0E3761]/30 hover:bg-white transition"
            title="Condividi"
            aria-label="Condividi"
          >
            <img src={shareIcon} alt="" className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClose?.();
            }}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-[#0E3761] bg-white/90 border border-[#0E3761]/30 hover:bg-white transition"
            title="Chiudi"
            aria-label="Chiudi"
          >
            <img src={closeIcon} alt="Chiudi" className="h-4 w-4" />
          </button>
        </div>

        <h2 className="text-[#c66529] text-[30px] md:text-[40px] font-bold">
          Modello di Business
        </h2>

        {/* Contenuto */}
        <div className="pt-24 pb-8 md:pt-14">
          {/* STAGE RELATIVO: il pannello si posiziona rispetto a questo wrapper */}
          <div
            ref={stageRef}
            className="relative flex flex-col items-center gap-5 max-w-[1080px] mx-auto"
          >
            {/* Toggle INPUT (sopra al cerchio) */}
            <div ref={inputBoxRef}>
              <ToggleStack
                section="input"
                activeKey={panel?.section === "input" ? panel.key : null}
                onOpen={openFrom("input")}
                label="INPUT"
              />
            </div>

            {/* Lottie */}
            <Lottie
              animationData={LottieAnimation}
              loop={false}
              autoplay
              className="w-full h-auto"
            />

            {/* Toggle OUTPUT (sotto al cerchio) */}
            <div ref={outputBoxRef}>
              <ToggleStack
                section="output"
                activeKey={panel?.section === "output" ? panel.key : null}
                onOpen={openFrom("output")}
                label="OUTPUT"
              />
            </div>

            {/* Pannello flottante */}
            {panel && activeItems?.length > 0 && (
              <FloatingPanel
                title={panel.key}
                items={activeItems}
                anchorY={panel.anchorY}
                onClose={() => setPanel(null)}
              />
            )}
          </div>
        </div>

        {/* CTA finale */}
        <div className="mt-4">
          <a href="https://www.realegroup.eu/groupshare/0/IT/ListaReportsAndAccounts/87/BILANCIO%20CONSOLIDATO%20RG%202025.pdf#page=15" target="_blank">
            <button
              type="button"
              className="bt-secondary2 text-sm px-4 py-2"
            >
              <img
                src={arrowIconWhite}
                alt="Arrow"
                className="inline-block mr-2 w-[20px] h-[20px]"
              />
              Consulta il Report - cap. 1 • par. 1.1.1
            </button>
          </a>
        </div>
      </div>
    </div>,
    document.body
  );
}
